import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountMarketplace } from "@front/m19-api-client";
import { Catalog } from "@front/m19-models";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { KeywordTrackingTimelineComponent } from "@m19-board/keyword-tracker/keyword-tracking-timeline/keyword-tracking-timeline.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable, of, switchMap, tap } from "rxjs";
import { KeywordTrackerService } from "../../services/keyword-tracker.service";
import { KeywordTrackerRow } from "../keyword-tracker-page.component";
import { LoadingService } from "@m19-board/services/loading.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";

type AddProductMessage = {
  type: "info" | "success" | "danger";
  message: string;
};

@UntilDestroy()
@Component({
  selector: "app-keyword-tracker-details",
  templateUrl: "./keyword-tracker-details.component.html",
  imports: [
    AsyncPipe,
    TranslocoDirective,
    KeywordTrackingTimelineComponent,
    IButtonDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IAlertComponent,
    NavBreadcrumbDirective,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordTrackerDetailsComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private accountSelection = inject(AccountSelectionService);
  private keywordTrackerService = inject(KeywordTrackerService);
  private asinService = inject(AsinService);
  private loadingService = inject(LoadingService);

  searchTerm$: Observable<KeywordTrackerRow | undefined> = this.keywordTrackerService.selectedSearchTerm$;
  keywordTrackerData = signal<KeywordTrackerRow[] | undefined>(this.keywordTrackerService.getKeywordTrackerData());

  accountMarketplace$: Observable<AccountMarketplace> = this.accountSelection.singleAccountMarketplaceSelection$;
  am?: AccountMarketplace;

  catalog?: Catalog;

  addProductMessage$: Observable<AddProductMessage | undefined> = this.keywordTrackerService.addProductMessage$;

  constructor() {
    const route = this.route;

    this.loadingService.loadingOn();

    route.params
      .pipe(
        untilDestroyed(this),
        tap(() => this.keywordTrackerService.setTableView(true)),
        switchMap((_) =>
          this.accountMarketplace$.pipe(
            untilDestroyed(this),
            tap((am: AccountMarketplace) => (this.am = am)),
            switchMap((am: AccountMarketplace) => {
              return combineLatest<[Catalog, KeywordTrackerRow[], string]>([
                this.asinService.getCatalog(am.accountId, am.marketplace),
                this.keywordTrackerService.keywordTrackerData$,
                of(this.route.snapshot.paramMap.get("searchterm") ?? ""),
              ]);
            }),
          ),
        ),
      )
      .subscribe(([catalog, keywordData, currentSearchTerm]: [Catalog, KeywordTrackerRow[], string]) => {
        this.keywordTrackerData.set(keywordData);
        const currentSearchTermData = keywordData.filter((d) => d.searchTerm === currentSearchTerm);
        this.keywordTrackerService.setSelectedSearchTerm(currentSearchTermData[0]);
        this.catalog = catalog;
      });

    this.searchTerm$.pipe(takeUntilDestroyed()).subscribe((_) => this.loadingService.loadingOff());
  }

  navigateToSearchTerm(searchTerm: KeywordTrackerRow) {
    const relatedData = this.keywordTrackerData()?.filter((d) => d.searchTerm === searchTerm.searchTerm);
    if (!relatedData || !relatedData.length) return; // should not go there

    this.keywordTrackerService.setSelectedSearchTerm(relatedData[0]);
    this.router
      .navigate(["/keyword-center/keyword-tracker/" + searchTerm.searchTerm], { queryParamsHandling: "merge" })
      .then();
  }
}
